<template>
    <div class="p-4 lg:p-8">
        <div class="surface-card p-4 shadow-sm border border-iron rounded-lg">

            <div class="mb-6">
                <h2 class="text-2xl font-bold text-secondary">{{ translate('My Purchased Materials') }}</h2>
                <p class="text-sm text-gray-500">
                    {{ translate('Below are the material packages you have purchased. Click details to view and download files.', 'ucfirst') }}
                </p>
            </div>

            <DataTable 
                :value="materialGroups" 
                :loading="loading" 
                dataKey="id"
                :paginator="true" 
                :rows="10"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                class="p-datatable-sm shadow-sm border border-iron rounded"
            >
                <template #empty>
                    {{ translate('no purchased material found.', 'ucfirst') }}
                </template>

                <Column field="title" :header="translate('title', 'ucfirst')" sortable class="font-semibold text-secondary" />

                <Column field="description" :header="translate('description', 'ucfirst')">
                    <template #body="slotProps">
                        <span class="text-sm text-gray-600 line-clamp-2">
                            {{ slotProps.data.description || translate('no description available.', 'ucfirst') }}
                        </span>
                    </template>
                </Column>

                <Column field="price" :header="translate('price', 'ucfirst')" class="w-32">
                    <template #body="slotProps">
                        <span class="text-sm font-medium text-gray-700">
                            {{ formatCurrency(slotProps.data.price) }}
                        </span>
                    </template>
                </Column>

                <Column class="w-36 text-center" header="Ações">
                    <template #body="slotProps">
                        <Button 
                            :label="translate('files', 'ucfirst')" 
                            icon="pi pi-folder-open" 
                            class="p-button-outlined p-button-sm text-xs"
                            @click="openMaterialsDialog(slotProps.data)"
                        />
                    </template>
                </Column>
            </DataTable>
        </div>

        <Dialog 
            v-model:visible="isModalOpen" 
            modal 
            :header="selectedGroup?.title || translate('package files', 'ucfirst')" 
            :style="{ width: '90vw', maxWidth: '600px' }"
            :draggable="false"
        >
            <div class="py-2">
                <h4 class="text-xs font-bold uppercase tracking-wider text-gray-500 mb-4">
                    {{ translate('files in this package', 'ucfirst') }} ({{ getMaterialsCount(selectedGroup) }})
                </h4>

                <div v-if="getMaterialsCount(selectedGroup) === 0" class="text-xs text-gray-400 italic py-4 text-center">
                    {{ translate('no files available in this package.', 'ucfirst') }}
                </div>

                <div v-else class="space-y-3">
                    <div 
                        v-for="material in selectedGroup.sellingMaterials" 
                        :key="material.id"
                        class="p-3 border border-iron rounded-lg flex items-center justify-between bg-gray-50"
                    >
                        <div class="pr-4">
                            <p class="text-sm font-semibold text-gray-800">{{ material.name }}</p>
                            <p v-if="material.description" class="text-xs text-gray-500 mt-0.5">
                                {{ material.description }}
                            </p>
                        </div>

                        <Button 
                            :label="isFileAvailable(material) ? translate('download', 'ucfirst') : translate('unavailable', 'ucfirst')" 
                            icon="pi pi-download"
                            class="p-button-sm no-bg-button text-xs px-3"
                            :disabled="!isFileAvailable(material)"
                            :loading="downloadingIds.has(material.id)" 
                            @click="downloadSingleFile(material)" 
                        />
                    </div>
                </div>
            </div>

            <template #footer>
                <Button :label="translate('close', 'ucfirst')" icon="pi pi-times" class="p-button-text" @click="isModalOpen = false" />
            </template>
        </Dialog>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue';
import { showToast } from '@/composables/toast';
import { translate } from '@/composables/translate';
import SellingMaterialService from '@/services/SellingMaterialService';

const route = useRoute();
const router = useRouter();
const toast = useToast();

const materialGroups = ref([]);
const loading = ref(false);
const downloadingIds = ref(new Set());

const isModalOpen = ref(false);
const selectedGroup = ref(null);

onMounted(() => {
    loadPurchasedMaterials();
});

const loadPurchasedMaterials = async () => {
    loading.value = true;
    try {
        materialGroups.value = await SellingMaterialService.get();
        checkAndOpenSelectedFromQuery();
    } catch (error) {
        showToast(toast, translate('error loading materials', 'ucfirst'), 'error');
        materialGroups.value = [];
    } finally {
        loading.value = false;
    }
};

const checkAndOpenSelectedFromQuery = () => {
    const selectedId = route.query.selected;
    if (!selectedId || !Array.isArray(materialGroups.value)) return;

    const group = materialGroups.value.find(
        (item) => String(item.id) === String(selectedId) || String(item.uuid) === String(selectedId)
    );

    if (group) {
        openMaterialsDialog(group);
    }

    const query = { ...route.query };
    delete query.selected;
    router.replace({ query });
};

const openMaterialsDialog = (group) => {
    selectedGroup.value = group;
    isModalOpen.value = true;
};

const isFileAvailable = (material) => {
    if (!material) return false;
    return material.hasFile === true || material.hasFile === 'has';
};

const getMaterialsCount = (group) => {
    return group?.sellingMaterials?.length || 0;
};

const downloadSingleFile = async (sellingMaterial) => {
    if (!isFileAvailable(sellingMaterial)) return;

    downloadingIds.value.add(sellingMaterial.id);
    
    const response = await SellingMaterialService.fetchDownloadData(sellingMaterial.id);
    downloadingIds.value.delete(sellingMaterial.id);
    
    if (!response.success()) {
        showToast(toast, response.getMessage(), 'error');
        return;
    }

    const url = window.URL.createObjectURL(new Blob([response.getContent()]));
    const link = document.createElement('a');
    link.href = url;
    
    const ext = typeof sellingMaterial.getExtension === 'function' 
        ? sellingMaterial.getExtension() 
        : '';

    link.setAttribute('download', sellingMaterial.name + ext);
    document.body.appendChild(link);
    link.click();
    link.remove();
};

const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};
</script>